import { Eye, FileText } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { Sheet } from '@/components/ui/Sheet'
import { useFormat } from '@/i18n/useFormat'
import { ApiError } from '@/lib/api'
import { CAP, can } from '@/lib/can'
import { useSession } from '@/features/auth/api/useSession'
import { useCaseArchive } from '@/features/cases/api/useCaseArchive'
import { RenderedDocument } from '@/features/finance/components/DocumentPreview'
import { mayOpenArchivedKind } from '@/features/finance/documentAccess'
import type { ArchivedDocument } from '@/types/domain'

/* «Бумаги работы» — the case's paper, read-only.
 *
 * ── Who it is for ──────────────────────────────────────────────────────────
 *
 * A reader holding `document.archive.read` and NEITHER of Finance's render
 * keys: Sales, Pricing and Operations as seeded. It heads the case's
 * «Документы» (S-11, `FilesSection`, by `readsCaseArchive`); a reader holding
 * either render key gets Finance's workspace (`CaseDocuments`) under «Счета
 * клиенту» in «Деньги» instead, and the old `?section=documents` lands each
 * reader on theirs.
 *
 * It answers the follow-up #51 left open when it took the «Счета» section away
 * from those roles: an Operations user running a shipment should be able to
 * tell that an invoice went out, without Finance's amounts, recipients or bank
 * block reaching them. The list is `GET /api/documents?sourceKind=case`, which
 * the server already serves to them and which carries existence only — kind,
 * number, dates, the issuing entity's trading name — so nothing new reaches any
 * role through this screen.
 *
 * ── What a row offers ──────────────────────────────────────────────────────
 *
 * Nothing Finance does: no raise, no issue, no schedule, no link to an invoice
 * page whose read is refused. Open only where the preview would answer
 * (`mayOpenArchivedKind`, the mirror of the server's `readCapability`): an
 * invoice or debit-note PDF prints the bank block and the recipient and opens
 * on Finance's key alone (#52), so those rows carry no control — a button whose
 * only answer is a 403 is worse than none. That rule is said ONCE, in the
 * subtitle, rather than as a refusal on every row.
 *
 * Opening reuses `RenderedDocument`, the one owner of the signed-URL rules: the
 * link is a credential, goes on a frame and never on an anchor, and is dropped
 * when the sheet closes.
 *
 * ── Which date ─────────────────────────────────────────────────────────────
 *
 * Issued paper is dated by its ISSUE stamp — the day it became binding, which
 * is what "when was it invoiced" asks. Anything never issued (a status update)
 * shows the date printed on it, and only that: the archive row records that a
 * status update was rendered and queued, not that it was delivered, so nothing
 * here says "sent".
 */

/** The kinds this build has a label for; anything else reads as "Document". */
const KNOWN_KINDS = new Set(['invoice', 'debit-note', 'offer', 'status-update'])

export function CaseArchiveList({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  const format = useFormat()
  const titleId = useId()
  const [openId, setOpenId] = useState<string | null>(null)

  const { data: user } = useSession()
  const mayRead = can(user, CAP.documentArchiveRead)
  const archive = useCaseArchive(caseId, mayRead)

  /* After every hook, since hooks cannot return early. The rail does not offer
     the section to a reader holding none of the three keys; this is what a
     pasted deep link meets. */
  if (!mayRead) return null

  const kindLabel = (kind: string) =>
    KNOWN_KINDS.has(kind)
      ? t(`documentKind.${kind as 'invoice' | 'debit-note' | 'offer' | 'status-update'}`)
      : t('cases.papers.unknownKind')

  const dateOf = (document: ArchivedDocument) =>
    document.issued && document.issuedAt !== undefined
      ? format.instantDate(document.issuedAt)
      : format.date(document.documentDate)

  /** What the Open control and the sheet call the document: its kind and its
   *  number, or its date when it has none. */
  const nameOf = (document: ArchivedDocument) =>
    `${kindLabel(document.kind)} ${document.number ?? dateOf(document)}`

  const openable = (document: ArchivedDocument) => mayOpenArchivedKind(user, document.kind)

  const opened = archive.data?.find((document) => document.id === openId)

  const openButton = (document: ArchivedDocument) => (
    <Button
      variant="ghost"
      size="sm"
      iconLeft={Eye}
      aria-label={t('cases.papers.openLabel', { document: nameOf(document) })}
      onClick={() => setOpenId(document.id)}
    >
      {t('common.actions.open')}
    </Button>
  )

  const identity = (document: ArchivedDocument) => (
    <span className="flex min-w-0 items-baseline gap-2">
      <span className="text-text-heading text-[13px]">{kindLabel(document.kind)}</span>
      {document.number === undefined ? null : (
        <span className="text-text-heading font-mono text-[12.5px] font-semibold">
          {document.number}
        </span>
      )}
    </span>
  )

  /* The three states and the list as a NODE, so the header — which says what
     this surface is and who issues the paper on it — stays on screen while the
     list loads or fails. */
  const body =
    archive.error instanceof ApiError && archive.error.isNotFound ? (
      /* The archive is mounted only where object storage is configured: a 404
         means "no archive on this deployment", which sends the reader to a
         different person than "no paper" would. */
      <EmptyState
        icon={FileText}
        title={t('cases.papers.title')}
        description={t('finance.documents.archiveUnavailable')}
      />
    ) : archive.isError ? (
      <div className="px-[18px] pb-4">
        <ErrorState error={archive.error} onRetry={() => void archive.refetch()} />
      </div>
    ) : archive.isPending || archive.data === undefined ? (
      <div className="px-[18px] pb-4" aria-busy="true" aria-label={t('cases.papers.loading')}>
        <Skeleton className="h-32 rounded-[var(--radius-DEFAULT)]" />
      </div>
    ) : archive.data.length === 0 ? (
      <EmptyState
        icon={FileText}
        title={t('cases.papers.emptyTitle')}
        description={t('cases.papers.emptyDescription')}
      />
    ) : (
      <>
        {/* Desktop and tablet: one dense table, nothing editable in it. */}
        <div className="hidden md:block">
          <table aria-labelledby={titleId} className="w-full border-collapse text-left">
            <thead>
              <tr className="border-line border-b">
                <th scope="col" className="asa-eyebrow h-9 px-[18px]">
                  {t('cases.papers.document')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3">
                  {t('common.fields.date')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3">
                  {t('finance.documents.entity')}
                </th>
                <th scope="col" className="h-9 w-px px-[18px]">
                  <span className="sr-only">{t('common.actions.label')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {archive.data.map((document) => (
                <tr
                  key={document.id}
                  className="border-line-hair hover:bg-surface-hover h-[38px] border-b transition-colors last:border-b-0"
                >
                  <td className="px-[18px]">{identity(document)}</td>
                  <td className="asa-num text-text-body px-3 text-[13px]">
                    {dateOf(document)}
                  </td>
                  <td className="text-text-body px-3 text-[13px]">{document.entityName}</td>
                  <td className="px-[18px] text-right">
                    {openable(document) ? openButton(document) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Phone: a row becomes a card, never a table scrolled sideways. */}
        <ul aria-labelledby={titleId} className="flex flex-col md:hidden">
          {archive.data.map((document) => (
            <li
              key={document.id}
              className="border-line-hair flex items-center gap-3 border-b px-4 py-3 last:border-b-0"
            >
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                {identity(document)}
                <span className="text-text-muted text-[12px] font-light">
                  <span className="asa-num">{dateOf(document)}</span>
                  {' · '}
                  {document.entityName}
                </span>
              </span>
              {openable(document) ? openButton(document) : null}
            </li>
          ))}
        </ul>
      </>
    )

  return (
    <Card>
      <CardHeader>
        <CardTitle id={titleId}>{t('cases.papers.title')}</CardTitle>
        <CardSubtitle>{t('cases.papers.subtitle')}</CardSubtitle>
      </CardHeader>

      {body}

      {/* Mounted only while a document is open, so the signed URL is asked for
          on the press and discarded on close (`gcTime: 0`). */}
      {opened === undefined ? null : (
        <Sheet
          open
          onClose={() => setOpenId(null)}
          title={nameOf(opened)}
          size="wide"
        >
          <RenderedDocument key={opened.id} documentId={opened.id} />
        </Sheet>
      )}
    </Card>
  )
}
